import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Skeleton, Stack, Typography, Card, CardContent, alpha, useTheme } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import TemplateForm from '../components/TemplateForm';
import { templatesApi } from '../../../api/templates.api';
import useUIStore from '../../../store/useUIStore';

const TemplateEditPage = ({ sess = { uid: '', role: 'user' } }) => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    return (
      data?.error?.error_user_msg ||
      data?.error?.message ||
      data?.message ||
      data?.error ||
      error?.message ||
      fallback
    );
  };

  const findNumericVariables = (text = '') => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = matches.map((m) => m.replace(/[{}]/g, '')).filter(Boolean);
    return Array.from(new Set(numbers)).sort((a, b) => Number(a) - Number(b));
  };

  const buildExampleArray = (vars = [], samples = {}) => {
    if (!vars.length) return undefined;
    return vars.map((v) => samples?.[v] || `sample_${v}`);
  };

  const normalizeLanguageCode = (value) => {
    const raw = `${value || ''}`.trim();
    if (!raw) return 'en_US';
    if (/^en[-_]us$/i.test(raw)) return 'en_US';
    return raw;
  };

  const normalizeLanguage = (tpl = {}) => {
    const lang = tpl.language;
    const objectLang =
      lang && typeof lang === 'object'
        ? lang.code || lang.value || lang.name || lang.label
        : null;
    const resolved =
      objectLang ||
      (typeof lang === 'string' ? lang : null) ||
      tpl.languageCode ||
      tpl.language_code ||
      tpl.lang ||
      tpl.locale ||
      tpl.t_language ||
      tpl.metaTemplateLanguage ||
      tpl.meta_template_language ||
      tpl.meta?.language ||
      'en_US';
    return normalizeLanguageCode(resolved);
  };

  const splitPhoneNumber = (value = '') => {
    const raw = `${value}`.trim();
    if (!raw) return { phoneCountryCode: '', phoneNumber: '' };

    const digits = raw.replace(/[^+\d]/g, '');
    const match = digits.match(/^(\+?\d{1,4})(\d{5,})$/);
    if (match) {
      const dial = match[1].startsWith('+') ? match[1] : `+${match[1]}`;
      return { phoneCountryCode: dial, phoneNumber: match[2] };
    }
    // Fallback: assume first 2-3 digits are dial code
    const fallbackDial = digits.slice(0, 3) || '';
    const fallbackNumber = digits.slice(3) || digits.replace(/^\+/, '');
    return {
      phoneCountryCode: fallbackDial ? (fallbackDial.startsWith('+') ? fallbackDial : `+${fallbackDial}`) : '',
      phoneNumber: fallbackNumber,
    };
  };

  const mapButtonsForForm = (buttons = []) => {
    if (!Array.isArray(buttons)) return [];
    return buttons
      .filter(Boolean)
      .map((rawBtn) => {
        const btn = rawBtn || {};
        const phoneParts = splitPhoneNumber(btn.phoneNumber || btn.phone_number || btn.value || '');
        const type = btn.type || btn.tb_type || btn.action || 'QUICK_REPLY';
        const urlType = btn.urlType || btn.url_type || (btn.urlVariable || btn.url_variable ? 'DYNAMIC' : 'STATIC') || 'STATIC';
        return {
          ...btn,
          type,
          text: btn.text || btn.label || btn.name || '',
          urlType,
          url: btn.url || btn.href || '',
          urlVariable: btn.urlVariable || btn.url_variable || '',
          phoneCountryCode: btn.phoneCountryCode || btn.phone_country_code || phoneParts.phoneCountryCode,
          phoneNumber: btn.phoneNumber || btn.phone_number || phoneParts.phoneNumber,
        };
      });
  };

  const mapVariableSamples = (tpl = {}) => {
    const baseSamples =
      tpl.variableSamples ||
      tpl.variable_samples ||
      tpl.variablesamples ||
      tpl.t_variable_samples ||
      {};
    const normalized = {
      header: baseSamples.header || baseSamples.Header || {},
      body: baseSamples.body || baseSamples.Body || {},
      footer: baseSamples.footer || baseSamples.Footer || {},
    };

    // Fallback: when backend returns variables array [{ tag: '1', example: 'foo' }]
    if (Array.isArray(tpl.variables)) {
      tpl.variables.forEach((v) => {
        const key = `${v?.tag || v?.name || ''}`.trim();
        if (key) {
          const example = v?.example || v?.sample || '';
          normalized.body[key] = normalized.body[key] || example;
        }
      });
    }

    // Fallback: when tags list is used
    if (Array.isArray(tpl.tags)) {
      tpl.tags.forEach((t) => {
        const key = `${t?.tagNumber || t?.tag || ''}`.trim();
        if (key) {
          const example = t?.value || t?.example || '';
          normalized.body[key] = normalized.body[key] || example;
        }
      });
    }

    return normalized;
  };
  const buildComponents = (data) => {
    const components = [];
    const headerVars = findNumericVariables(data.header);
    const bodyVars = findNumericVariables(data.body);
    const headerSampleArray = buildExampleArray(headerVars, data.variableSamples?.header);
    const bodySampleArray = buildExampleArray(bodyVars, data.variableSamples?.body);

    if (data.headerType && data.headerType !== 'none') {
      if (data.headerType === 'text' && data.header) {
        const headerComponent = { type: 'HEADER', format: 'TEXT', text: data.header };
        if (headerSampleArray) {
          headerComponent.example = { header_text: headerSampleArray };
        }
        components.push(headerComponent);
      } else if (['image', 'video', 'document'].includes(data.headerType)) {
        const headerComponent = {
          type: 'HEADER',
          format: data.headerType.toUpperCase(),
        };
        if (data.headerHandle) {
          headerComponent.example = { header_handle: [data.headerHandle] };
        }
        components.push(headerComponent);
      }
    }

    if (data.body) {
      const bodyComponent = { type: 'BODY', text: data.body };
      if (bodySampleArray) {
        bodyComponent.example = { body_text: [bodySampleArray] };
      }
      components.push(bodyComponent);
    }

    if (data.footer) {
      components.push({ type: 'FOOTER', text: data.footer });
    }

    if (Array.isArray(data.buttons) && data.buttons.length) {
      const buttons = data.buttons.map((btn) => {
        if (btn.type === 'URL') {
          return {
            type: 'URL',
            text: btn.text,
            urlType: btn.urlType,
            url: btn.url,
            urlVariable: btn.urlVariable,
            value: btn.url || '',
          };
        }
        if (btn.type === 'PHONE_NUMBER') {
          const phoneValue = `${btn.phoneCountryCode || ''}${btn.phoneNumber || ''}`;
          return {
            type: 'PHONE_NUMBER',
            text: btn.text,
            phoneNumber: phoneValue,
            phone_number: phoneValue,
            value: phoneValue,
          };
        }
        return { type: 'QUICK_REPLY', text: btn.text };
      });
      components.push({ type: 'BUTTONS', buttons });
    }

    return components;
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await templatesApi.getById(id);
        const tpl = response?.data?.data || response?.data || {};

        const components =
          tpl.components ||
          tpl.templateComponents ||
          tpl.preview?.components ||
          tpl.message_template_components ||
          [];
        const headerComponent = components.find((c) => c.type === 'HEADER') || {};
        const bodyComponent = components.find((c) => c.type === 'BODY') || {};
        const footerComponent = components.find((c) => c.type === 'FOOTER') || {};
        const buttonsComponent =
          components.find((c) => c.type === 'BUTTONS') ||
          components.find((c) => c.component_type === 'BUTTONS') ||
          {};
        const componentButtons =
          buttonsComponent.buttons ||
          buttonsComponent.content?.buttons ||
          buttonsComponent.button_list ||
          [];

        const headerHandle =
          tpl.headerHandle ||
          tpl.header_handle ||
          tpl.header?.example?.header_handle?.[0] ||
          headerComponent.example?.header_handle?.[0] ||
          tpl.header?.header_handle ||
          tpl.t_file ||
          '';
        const headerText =
          (typeof tpl.header === 'string' && tpl.header) ||
          tpl.header?.text ||
          tpl.t_header ||
          headerComponent.text ||
          '';
        const bodyText =
          (typeof tpl.body === 'string' && tpl.body) ||
          tpl.body?.text ||
          tpl.t_btext ||
          bodyComponent.text ||
          '';
        const footerText =
          (typeof tpl.footer === 'string' && tpl.footer) ||
          tpl.footer?.text ||
          tpl.t_footer ||
          footerComponent.text ||
          '';
        const buttonList = Array.isArray(tpl.buttons)
          ? tpl.buttons
          : Array.isArray(tpl.buttons?.items)
            ? tpl.buttons.items
            : Array.isArray(componentButtons)
              ? componentButtons
              : Array.isArray(tpl.buttonsList)
                ? tpl.buttonsList
                : tpl.t_buttons || [];

        const variableSamples = mapVariableSamples(tpl);

        setFormData({
          name: tpl.name || tpl.t_tname || '',
          language: normalizeLanguage(tpl),
          category: tpl.category || tpl.t_type || tpl.metaTemplateCategory || 'UTILITY',
          headerType: (
            tpl.headerType ||
            tpl.t_header_type ||
            headerComponent.format ||
            headerComponent.header_format ||
            headerComponent.media_type ||
            'none'
          ).toLowerCase(),
          header: headerText,
          headerMediaUrl:
            tpl.headerMediaUrl ||
            tpl.t_header_media_url ||
            tpl.header?.mediaUrl ||
            tpl.header?.url ||
            tpl.header?.file ||
            headerComponent.mediaUrl ||
            headerComponent.url ||
            headerComponent.media_url ||
            '',
          headerHandle,
          body: bodyText,
          footer: footerText,
          buttons: mapButtonsForForm(buttonList),
          variableSamples,
        });
        setStatus({ type: '', message: '' });
      } catch (err) {
        setStatus({ type: 'error', message: 'Error loading template' });
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [sess.uid, id]);

  const handleSubmit = async (data) => {
    setStatus({ type: '', message: '' });
    setSubmitting(true);
    try {
      const normalizedLanguage = normalizeLanguageCode(data.language);
      const normalizedCategory = (data.category || '').trim().toUpperCase();
      const payload = {
        name: data.name,
        category: normalizedCategory,
        language: normalizedLanguage,
        headerType: data.headerType,
        header: data.headerType === 'text' ? data.header : null,
        headerHandle: data.headerType !== 'text' && data.headerType !== 'none' ? data.headerHandle : null,
        body: data.body,
        footer: data.footer,
        buttons: data.buttons,
        components: buildComponents(data),
      };
      const response = await templatesApi.update(id, payload);
      const responseData = response?.data || {};
      if (responseData?.success === false || responseData?.error) {
        const message = getApiErrorMessage({ response: { data: responseData } }, 'Failed to update template');
        setStatus({ type: 'error', message });
        showSnackbar({ message, severity: 'error' });
        return;
      }
      const message = response?.data?.message || 'Template updated successfully';
      setStatus({ type: 'success', message });
      showSnackbar({ message, severity: 'success' });
      navigate('/templates', { replace: true });
      setTimeout(() => {
        window.location.assign('/templates');
      }, 50);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update template');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
          <Skeleton variant="rounded" width={90} height={36} />
          <Skeleton variant="text" width={180} height={28} />
        </Stack>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={140} />
          <Skeleton variant="rounded" height={240} />
          <Skeleton variant="rounded" height={140} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(-1);
              }}
              sx={secondaryButtonSx}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h5" fontWeight={700}>Edit Template</Typography>
              <Typography variant="body2" color="text.secondary">
                Update content, variables, and media
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {status.message && status.type === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.message}
        </Alert>
      )}

      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <TemplateForm
          template={formData}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
          loading={submitting}
          isEdit
        />
      </Box>
    </Box>
  );
};

export default TemplateEditPage;
